import { SIGNS, TILE_KIND, TILE_SPECIAL } from './type';
import {
  cloneBoard,
  makeSignTile,
  getTile,
  createInitialBoard,
  pickWeightedSignKey,
  WORLD_ELEMENT_REFILL_WEIGHT,
} from './board';
import { findMatches } from './matches';
import { sumMatchScores, scoreForActivations } from './scoring';
import { hasAnyValidMove, isSwapValid } from './swap';

// ================================
// DEBUG (resolver logs)
// ================================
// Toggle to inspect cascades / clears / activations in the browser console.
// Keep FALSE in normal dev to avoid console spam.
const DEBUG_RESOLVE_LOGS = false;

// IMPORTANT PERF NOTE:
// Guard debug payload creation with `if (DEBUG_RESOLVE_LOGS)` at call sites,
// so we never build large objects on mobile unless logging is enabled.
function _rzDbg(tag, payload) {
  if (!DEBUG_RESOLVE_LOGS) return;
  try {
    // eslint-disable-next-line no-console
    console.log(`[ZC_RESOLVE] ${tag}`, payload);
  } catch {
    // ignore
  }
}

// Resolver engine: clear -> gravity -> refill, 반복 hasta estable.
// Devuelve events para animación futura.

// ================================
// EMPTY/Hole handling
// ================================
// We keep ONE interactive "hole" on the board after each resolve.
// UI can swap into it because it is a real tile object (not null).
function isEmptyCell(t) {
  return t == null || t.isEmpty === true || t.kind === 'EMPTY' || t.kind === 'empty' || t.key === '+';
}

function makeEmptyHoleTile() {
  return {
    id: `hole_${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`,
    kind: 'EMPTY',
    key: '+',
    isEmpty: true,
    special: null,
  };
}

function applyGravity(board, anchorHole = null) {
  const events = [];

  for (let c = 0; c < board.cols; c++) {
    const anchorR =
      anchorHole &&
      Number.isFinite(anchorHole.r) &&
      Number.isFinite(anchorHole.c) &&
      anchorHole.c === c &&
      anchorHole.r >= 0 &&
      anchorHole.r < board.rows
        ? anchorHole.r
        : null;

    let writeR = board.rows - 1;

    for (let r = board.rows - 1; r >= 0; r--) {
      // Keep the anchor position EMPTY and FIXED in place.
      if (anchorR !== null && r === anchorR) {
        // Ensure it stays empty.
        board.grid[r][c] = null;
        writeR = r - 1;
        continue;
      }

      const t = board.grid[r][c];
      if (isEmptyCell(t)) continue;

      // Never write into the anchored hole.
      while (anchorR !== null && writeR === anchorR) writeR -= 1;

      if (writeR !== r) {
        board.grid[writeR][c] = t;
        board.grid[r][c] = null;
        events.push({ type: 'fall', id: t.id, from: { r, c }, to: { r: writeR, c } });
      }

      writeR -= 1;
    }

    // Final safety: keep the anchor empty.
    if (anchorR !== null) {
      board.grid[anchorR][c] = null;
    }
  }

  return events;
}

function createsImmediateMatchOnRefill(board, r, c, key) {
  // Similar al initial-board: evitar generar match inmediato por refill.
  // izquierda-izquierda
  const a = getTile(board, r, c - 1);
  const b = getTile(board, r, c - 2);
  if (
    a &&
    b &&
    a.kind === TILE_KIND.SIGN &&
    b.kind === TILE_KIND.SIGN &&
    a.key === key &&
    b.key === key
  ) {
    return true;
  }

  // arriba-arriba
  const u1 = getTile(board, r - 1, c);
  const u2 = getTile(board, r - 2, c);
  if (
    u1 &&
    u2 &&
    u1.kind === TILE_KIND.SIGN &&
    u2.kind === TILE_KIND.SIGN &&
    u1.key === key &&
    u2.key === key
  ) {
    return true;
  }

  return false;
}



function _listEmptyCells(board) {
  const empties = [];
  for (let r = 0; r < board.rows; r++) {
    for (let c = 0; c < board.cols; c++) {
      if (isEmptyCell(board.grid[r][c])) empties.push({ r, c });
    }
  }
  return empties;
}

function _samePos(a, b) {
  return !!a && !!b && a.r === b.r && a.c === b.c;
}

function _distManhattan(a, b) {
  if (!a || !b) return Number.POSITIVE_INFINITY;
  return Math.abs(a.r - b.r) + Math.abs(a.c - b.c);
}

function _closestEmptyCell(empties, ref) {
  if (!Array.isArray(empties) || empties.length === 0) return null;
  if (!ref) return empties[0];

  let best = empties[0];
  let bestD = _distManhattan(best, ref);
  for (const e of empties) {
    const d = _distManhattan(e, ref);
    if (d < bestD) {
      best = e;
      bestD = d;
    }
  }
  return best;
}

function _readHoleMeta(board) {
  const h = board?.meta?.hole;
  if (!h || typeof h.r !== 'number' || typeof h.c !== 'number') return null;
  return { r: h.r, c: h.c };
}

function _writeHoleMeta(board, pos) {
  if (!board || !pos) return;
  board.meta = board.meta || {};
  board.meta.hole = { r: pos.r, c: pos.c };
}

function _synchronizeHoleMeta(board) {
  let holePosition = null;
  let holeCount = 0;
  for (let r = 0; r < board.rows; r++) {
    for (let c = 0; c < board.cols; c++) {
      if (!isEmptyCell(board.grid[r][c])) continue;
      holePosition = { r, c };
      holeCount += 1;
    }
  }
  board.meta = board.meta || {};
  if (holeCount <= 1) board.meta.hole = holeCount === 1 ? holePosition : null;
  return board;
}

// Fill ALL empty cells except ONE (so the board stays playable with exactly 1 hole).
// This prevents accumulating unusable holes across the board.
function refillKeepOneHole(board, rng, preferHole = null) {
  const empties = _listEmptyCells(board);
  if (empties.length === 0) return [];

  // Pick which cell to keep as the single interactive hole.
  // Priority (premium UX):
  //  1) preferHole (usually ctx.swapTo)
  //  2) previous hole stored in board.meta.hole (if still empty)
  //  3) closest empty to the previous hole (stable / non-random)
  //  4) fallback: first empty
  let keep = null;

  const prevHole = _readHoleMeta(board);

  if (
    preferHole &&
    inBounds(board, preferHole.r, preferHole.c) &&
    isEmptyCell(board.grid[preferHole.r][preferHole.c])
  ) {
    keep = { r: preferHole.r, c: preferHole.c };
  } else if (
    prevHole &&
    inBounds(board, prevHole.r, prevHole.c) &&
    isEmptyCell(board.grid[prevHole.r][prevHole.c])
  ) {
    keep = { r: prevHole.r, c: prevHole.c };
  } else {
    keep = _closestEmptyCell(empties, prevHole) || empties[0];
  }

  // Ensure the kept cell is an INTERACTIVE hole tile (so UI can swap into it)
  // IMPORTANT: do not recreate the hole tile if it already exists (keeps stable id -> less re-mount / less lag)
  if (keep && inBounds(board, keep.r, keep.c)) {
    const existing = board.grid[keep.r][keep.c];
    const alreadyHole = !!existing && existing.isEmpty === true && (existing.kind === 'EMPTY' || existing.kind === 'empty') && existing.key === '+';

    if (!alreadyHole) {
      board.grid[keep.r][keep.c] = makeEmptyHoleTile();
    }

    _writeHoleMeta(board, keep);
  }

  const events = [];

  for (const pos of empties) {
    if (_samePos(pos, keep)) continue;

    // Choose a sign trying to avoid immediate match on refill.
    let attempts = 0;
    const worldElement = board?.meta?.biasWeight != null
      ? (board?.meta?.biasElement || null)
      : null;
    let key = pickWeightedSignKey(rng, {
      worldElement,
      biasWeight: worldElement ? WORLD_ELEMENT_REFILL_WEIGHT : null,
      allowedSigns: board?.meta?.allowedSigns || SIGNS,
    });
    while (attempts < 40 && createsImmediateMatchOnRefill(board, pos.r, pos.c, key)) {
      key = pickWeightedSignKey(rng, {
        worldElement,
        biasWeight: worldElement ? WORLD_ELEMENT_REFILL_WEIGHT : null,
        allowedSigns: board?.meta?.allowedSigns || SIGNS,
      });
      attempts += 1;
    }

    const t = makeSignTile(key);
    board.grid[pos.r][pos.c] = t;
    events.push({ type: 'spawn', id: t.id, at: { r: pos.r, c: pos.c }, key });
  }

  return events;
}

function shuffleBoard(b, rng) {
  const nb = cloneBoard(b);

  const coords = [];
  const signs = [];

  for (let r = 0; r < nb.rows; r++) {
    for (let c = 0; c < nb.cols; c++) {
      const t = nb.grid[r][c];
      if (t && t.kind === TILE_KIND.SIGN) {
        coords.push({ r, c });
        signs.push(t);
      }
    }
  }

  rng.shuffle(signs);

  coords.forEach((pos, i) => {
    nb.grid[pos.r][pos.c] = signs[i];
  });

  return nb;
}

function inBounds(board, r, c) {
  return r >= 0 && c >= 0 && r < board.rows && c < board.cols;
}

function pushUnique(setKey, r, c) {
  setKey.add(`${r},${c}`);
}

function keyToPos(k) {
  const [rs, cs] = k.split(',');
  return { r: Number(rs), c: Number(cs) };
}

// ================================
// Cosmic Charge (premium fun): every match fills a meter.
// When it reaches a threshold, we spawn ONE persistent special on an anchor.
// This makes even 3-matches feel rewarding.
// ================================
const COSMIC_CHARGE_THRESHOLD = 3; // tune: 3 => very frequent rewards (every few 3-matches)

function _readCharge(board) {
  const v = board?.meta?.charge;
  // We store charge as an integer.
  return Number.isFinite(v) ? Math.max(0, Math.floor(v)) : 0;
}

function _writeCharge(board, v) {
  if (!board) return;
  board.meta = board.meta || {};
  board.meta.charge = Math.max(0, Math.floor(v || 0));
  // Expose target so UI can render a consistent meter.
  board.meta.chargeTarget = COSMIC_CHARGE_THRESHOLD;
}

function _rand01(rng) {
  try {
    if (rng && typeof rng.random === 'function') return rng.random();
    if (rng && typeof rng.float === 'function') return rng.float();
    if (rng && typeof rng.next === 'function') return rng.next();
  } catch {
    // ignore
  }
  return Math.random();
}
 
function _pickChargeSpecial(rng, preferOrient = null) {
  // weights: 85% stripe, 13% bomb, 2% star
  const x = _rand01(rng);

  // ⚡ STRIPE (most frequent)
  if (x < 0.85) {
    let orient = preferOrient;
    if (orient !== 'h' && orient !== 'v') orient = _rand01(rng) < 0.5 ? 'h' : 'v';
    return orient === 'v' ? TILE_SPECIAL.STRIPE_V : TILE_SPECIAL.STRIPE_H;
  }

  // 💣 BOMB
  if (x < 0.98) return TILE_SPECIAL.BOMB;

  // ✨ STAR
  return TILE_SPECIAL.STAR;
}

function _chargeDeltaForGroup(g) {
  const n = Array.isArray(g?.cells) ? g.cells.length : 0;
  if (n >= 5) return 3;
  if (n === 4) return 2;
  if (n === 3) return 1;
  return 0;
}


function expandStripe(board, r, c, orient, toClear) {
  if (orient === 'v') {
    for (let rr = 0; rr < board.rows; rr++) pushUnique(toClear, rr, c);
  } else {
    for (let cc = 0; cc < board.cols; cc++) pushUnique(toClear, r, cc);
  }
}

function expandStar(board, targetKey, toClear) {
  if (!targetKey) return;
  for (let r = 0; r < board.rows; r++) {
    for (let c = 0; c < board.cols; c++) {
      const t = board.grid[r][c];
      if (!t || isEmptyCell(t)) continue;
      if (t.kind === TILE_KIND.SIGN && t.key === targetKey) {
        pushUnique(toClear, r, c);
      }
    }
  }
}

function expandBomb(board, r, c, toClear, radius = 1) {
  // Area-of-effect clear (Candy-like “bomb”): clears a square around (r,c).
  // radius=1 => 3x3.
  for (let rr = r - radius; rr <= r + radius; rr++) {
    for (let cc = c - radius; cc <= c + radius; cc++) {
      if (!inBounds(board, rr, cc)) continue;
      pushUnique(toClear, rr, cc);
    }
  }
}

// === Helper functions for special activation ===
function _addCellToClear(board, toClear, rr, cc) {
  if (!inBounds(board, rr, cc)) return;
  pushUnique(toClear, rr, cc);
}

function _activateStripe(board, toClear, activated, rr, cc, special, cause = null) {
  const orient = special === TILE_SPECIAL.STRIPE_V ? 'v' : 'h';

  const beforeSize = toClear.size;
  expandStripe(board, rr, cc, orient, toClear);
  const added = Math.max(0, toClear.size - beforeSize);

  const entry = { special, at: { r: rr, c: cc }, clearedCount: added };
  if (cause) entry.cause = cause;
  activated.push(entry);
}

function _activateStar(board, toClear, activated, rr, cc, targetKey, cause = null) {
  const beforeSize = toClear.size;
  expandStar(board, targetKey || null, toClear);
  const added = Math.max(0, toClear.size - beforeSize);

  const entry = {
    special: TILE_SPECIAL.STAR,
    at: { r: rr, c: cc },
    targetKey: targetKey || null,
    clearedCount: added,
  };
  if (cause) entry.cause = cause;
  activated.push(entry);
}

function _activateBomb(board, toClear, activated, rr, cc, cause = null) {
  const beforeSize = toClear.size;
  expandBomb(board, rr, cc, toClear, 1);
  const added = Math.max(0, toClear.size - beforeSize);

  const entry = { special: TILE_SPECIAL.BOMB, at: { r: rr, c: cc }, radius: 1, clearedCount: added };
  if (cause) entry.cause = cause;
  activated.push(entry);
}

function _isStripeSpecialValue(special) {
  return special === TILE_SPECIAL.STRIPE_H || special === TILE_SPECIAL.STRIPE_V;
}

function _countStripeActivations(activated) {
  if (!Array.isArray(activated) || activated.length === 0) return 0;
  let n = 0;
  for (const a of activated) {
    if (_isStripeSpecialValue(a?.special)) n += 1;
  }
  return n;
}

function _countStripeActivationsByCause(activated, cause) {
  if (!Array.isArray(activated) || activated.length === 0) return 0;
  let n = 0;
  for (const a of activated) {
    if (!_isStripeSpecialValue(a?.special)) continue;
    if ((a?.cause || null) === cause) n += 1;
  }
  return n;
}

function _countStripeActivationsWithoutCause(activated) {
  if (!Array.isArray(activated) || activated.length === 0) return 0;
  let n = 0;
  for (const a of activated) {
    if (!_isStripeSpecialValue(a?.special)) continue;
    if (!a?.cause) n += 1;
  }
  return n;
}

function _makeStripeActivationBreakdown(activated, eventCause = null) {
  // createdAuto: stripes produced by 4-match that auto-fire
  const createdAuto = _countStripeActivationsByCause(activated, 'created_stripe_auto');

  // bySpecialSwap: stripes activated because the player swapped a special
  const bySpecialSwap = _countStripeActivationsByCause(activated, 'special_swap') +
    (eventCause === 'special_swap' ? _countStripeActivationsWithoutCause(activated) : 0);

  const total = _countStripeActivations(activated);
  const existing = Math.max(0, total - createdAuto - bySpecialSwap);

  return { total, createdAuto, bySpecialSwap, existing };
}

function _hasImmediateMatches(board) {
  const m = findMatches(board);
  return Array.isArray(m) && m.length > 0;
}

function _applyMechanicDamage(board, clearedCells) {
  const state = board?.meta?.mechanicState;
  if (!Array.isArray(state)) return { damaged: 0, destroyed: 0 };
  let damaged = 0;
  let destroyed = 0;
  const touched = new Set();
  for (const cell of clearedCells || []) {
    for (const p of [{ r: cell.r, c: cell.c }, { r: cell.r - 1, c: cell.c }, { r: cell.r + 1, c: cell.c }, { r: cell.r, c: cell.c - 1 }, { r: cell.r, c: cell.c + 1 }]) {
      const key = `${p.r}:${p.c}`;
      if (touched.has(key) || p.r < 0 || p.c < 0 || p.r >= board.rows || p.c >= board.cols) continue;
      touched.add(key);
      const mechanic = state[p.r]?.[p.c];
      if (!mechanic) continue;
      mechanic.hp -= 1;
      damaged += 1;
      if (mechanic.hp <= 0) {
        state[p.r][p.c] = null;
        destroyed += 1;
      }
    }
  }
  return { damaged, destroyed };
}

// Si hay un especial en el tablero, consideramos que existe al menos un movimiento jugable
// porque el jugador puede activarlo intercambiándolo con un vecino.
// Esto evita falsos “tablero muerto” cuando no hay matches normales pero sí especiales.
function _hasAnySpecialMove(board) {
  for (let r = 0; r < board.rows; r++) {
    for (let c = 0; c < board.cols; c++) {
      const t = board.grid[r][c];
      if (!t || !t.special) continue;

      const neighbors = [
        { r, c: c - 1 },
        { r, c: c + 1 },
        { r: r - 1, c },
        { r: r + 1, c },
      ];

      for (const nb of neighbors) {
        if (!inBounds(board, nb.r, nb.c)) continue;
        // Preferimos respetar la lógica real del swap si existe
        try {
          if (isSwapValid(board, { r, c }, nb)) return true;
        } catch {
          // Fallback ultra defensivo: si la validación falla por algún motivo,
          // igual consideramos que el especial hace el tablero jugable.
          return true;
        }
      }

      // Tiene especial pero está aislado en bordes: igual lo consideramos jugable
      return true;
    }
  }
  return false;
}

function _hasAnyPlayableMove(board) {
  // Primero el chequeo “clásico” por match
  if (hasAnyValidMove(board)) return true;
  // Luego permitimos moves por especiales
  return _hasAnySpecialMove(board);
}

// Útil para el UI: asegura que un tablero “en reposo” (sin matches) tenga al menos un movimiento.
// Devuelve { board, events } donde events puede incluir un 'shuffle'.
export function ensurePlayableBoard(board, rng) {
  let b = cloneBoard(board);

  // Si el tablero está listo pero sin movimientos, lo arreglamos.
  if (!_hasAnyPlayableMove(b) || _hasImmediateMatches(b)) {
    const fix = ensurePlayableRestingBoard(b, rng, { maxShuffles: 35 });
    b = fix.board;
    if (fix.changed) {
      return { board: b, events: [{ type: 'shuffle', reason: fix.reason || 'shuffle' }] };
    }
  }

  return { board: b, events: [] };
}

function ensurePlayableRestingBoard(b, rng, { maxShuffles = 25 } = {}) {
  // Candy-crush behavior: when the board is "resting" (no animations pending)
  // it must have at least one valid move. Also avoid ending on immediate matches,
  // otherwise the game can auto-chain without a user move.

  const okResting = (bb) => _hasAnyPlayableMove(bb) && !_hasImmediateMatches(bb);
  const persistentHole = _readHoleMeta(b);
  const persistentHoleTile = persistentHole &&
    inBounds(b, persistentHole.r, persistentHole.c) &&
    isEmptyCell(b.grid[persistentHole.r][persistentHole.c])
    ? b.grid[persistentHole.r][persistentHole.c]
    : null;

  // Fast path
  if (okResting(b)) {
    return { board: b, changed: false, reason: null };
  }

  // Try shuffling in-place a few times
  for (let i = 0; i < maxShuffles; i++) {
    b = shuffleBoard(b, rng);
    if (okResting(b)) {
      return { board: b, changed: true, reason: 'shuffle' };
    }
  }

  // Worst-case fallback: rebuild a fresh playable board.
  // `createInitialBoard` already avoids immediate matches and ensures playability.
  const rebuilt = createInitialBoard(
    rng,
    b.rows,
    b.cols,
    b.meta?.biasWeight != null
      ? {
        worldKey: b.meta?.worldKey || null,
        level: b.meta?.level || 1,
        allowedSigns: b.meta?.allowedSigns || null,
        elementWeights: b.meta?.elementWeights || null,
        biasElement: b.meta?.biasElement || null,
        biasStrength: b.meta?.biasStrength ?? 0.68,
        biasWeight: b.meta?.biasWeight,
      }
      : undefined
  );
  if (persistentHole && persistentHoleTile) {
    rebuilt.grid[persistentHole.r][persistentHole.c] = { ...persistentHoleTile };
    _writeHoleMeta(rebuilt, persistentHole);
  }
  return { board: rebuilt, changed: true, reason: 'rebuild' };
}

export function resolveUntilStable(board, rng, maxIters = 30, ctx = null) {
  let b = cloneBoard(board);
  _synchronizeHoleMeta(b);
  // Keep charge target in sync with the engine threshold (UI reads this).
  b.meta = b.meta || {};
  b.meta.chargeTarget = COSMIC_CHARGE_THRESHOLD;
  let totalScore = 0;
  if (DEBUG_RESOLVE_LOGS) {
    _rzDbg('start', {
      rows: b?.rows,
      cols: b?.cols,
      maxIters,
      hasCtx: !!ctx,
      ctx: ctx ? { swapFrom: ctx.swapFrom, swapTo: ctx.swapTo, swapAxis: ctx.swapAxis } : null,
      hole: _readHoleMeta(b),
      charge: b?.meta?.charge,
      chargeTarget: b?.meta?.chargeTarget,
    });
  }
  const allEvents = [];
  const states = [];
  const makeStateBase = (phase, extra = {}) => {
    // Allow extra payload (fallEvents/spawnEvents/reason, etc.) while keeping
    // a consistent top-level shape for the UI.
    const stripesActivationBreakdown = extra.stripesActivationBreakdown || {
      total: 0,
      createdAuto: 0,
      bySpecialSwap: 0,
      existing: 0,
    };

    return {
      ...extra,
      phase,
      cause: extra.cause,
      scoreDelta: Number(extra.scoreDelta || 0) || 0,
      activated: Array.isArray(extra.activated) ? extra.activated : [],
      stripesActivated: Number(extra.stripesActivated || 0) || 0,
      stripesActivationBreakdown,
      clearedIds: Array.isArray(extra.clearedIds) ? extra.clearedIds : [],
      clearedCells: Array.isArray(extra.clearedCells) ? extra.clearedCells : [],
      matches: Array.isArray(extra.matches) ? extra.matches : undefined,
      groups: Array.isArray(extra.groups) ? extra.groups : undefined,
      board: extra.board,
    };
  };
  const ZERO_STRIPES = { total: 0, createdAuto: 0, bySpecialSwap: 0, existing: 0 };

  for (let iter = 0; iter < maxIters; iter++) {
    if (DEBUG_RESOLVE_LOGS) {
      _rzDbg('iter_begin', {
        iter,
        hole: _readHoleMeta(b),
        charge: b?.meta?.charge,
        chargeTarget: b?.meta?.chargeTarget,
      });
    }
    // =========================================================
    // Candy-like: si el swap inicial incluye especiales, se activan
    // aunque no haya match (ej: stripe limpia fila/col; star limpia un signo)
    // =========================================================
    if (iter === 0 && ctx && ctx.swapFrom && ctx.swapTo) {
      const a = ctx.swapFrom;
      const bpos = ctx.swapTo;
      const tA = b.grid?.[a.r]?.[a.c] || null;
      const tB = b.grid?.[bpos.r]?.[bpos.c] || null;

      const isStripe = (t) => t && (t.special === TILE_SPECIAL.STRIPE_H || t.special === TILE_SPECIAL.STRIPE_V);
      const isStar = (t) => t && t.special === TILE_SPECIAL.STAR;
      const isBomb = (t) => t && t.special === TILE_SPECIAL.BOMB;
      const isEmptyHole = (t) => isEmptyCell(t);

      const hasSpecial =
        isStripe(tA) || isStripe(tB) ||
        isStar(tA) || isStar(tB) ||
        isBomb(tA) || isBomb(tB);

      if (hasSpecial) {
        const toClear = new Set();
        const activated = []; // [{ special, at:{r,c}, targetKey? }]

        // Candy-like combos: STAR+STAR => clears the whole board.
        // (Keep exactly one hole tile; holes are considered empty and are skipped.)
        const starStar = isStar(tA) && isStar(tB);

        if (starStar) {
          // Clear every non-empty cell (including specials), but never clear the interactive hole.
          for (let rr = 0; rr < b.rows; rr++) {
            for (let cc = 0; cc < b.cols; cc++) {
              const tt = b.grid[rr][cc];
              if (!tt || isEmptyHole(tt)) continue;
              _addCellToClear(b, toClear, rr, cc);
            }
          }

          // Record both stars as activations (use a specific cause for clarity).
          activated.push({ special: TILE_SPECIAL.STAR, at: { r: a.r, c: a.c }, cause: 'special_swap_star_star' });
          activated.push({ special: TILE_SPECIAL.STAR, at: { r: bpos.r, c: bpos.c }, cause: 'special_swap_star_star' });
        } else {
          // Siempre consumimos las dos celdas del swap cuando hay activación
          _addCellToClear(b, toClear, a.r, a.c);
          _addCellToClear(b, toClear, bpos.r, bpos.c);
        }

        // Star target: el signo con el que se swapea (si existe)
        const targetKeyForA = tB && tB.kind === TILE_KIND.SIGN ? tB.key : (tA && tA.kind === TILE_KIND.SIGN ? tA.key : null);
        const targetKeyForB = tA && tA.kind === TILE_KIND.SIGN ? tA.key : (tB && tB.kind === TILE_KIND.SIGN ? tB.key : null);

        // Activaciones (pueden ser 1 o 2 especiales)
        if (!starStar) {
          if (isStripe(tA)) _activateStripe(b, toClear, activated, a.r, a.c, tA.special, 'special_swap');
          if (isStripe(tB)) _activateStripe(b, toClear, activated, bpos.r, bpos.c, tB.special, 'special_swap');

          // STAR activación normal: limpia el signo objetivo (si hay), si no hay target no hace nada.
          if (isStar(tA)) _activateStar(b, toClear, activated, a.r, a.c, targetKeyForA, 'special_swap');
          if (isStar(tB)) _activateStar(b, toClear, activated, bpos.r, bpos.c, targetKeyForB, 'special_swap');

          if (isBomb(tA)) _activateBomb(b, toClear, activated, a.r, a.c, 'special_swap');
          if (isBomb(tB)) _activateBomb(b, toClear, activated, bpos.r, bpos.c, 'special_swap');
        }

        // Aplicar clear de activación directa
        const clearedCells = [];
        for (const k of toClear.values()) {
          const { r, c } = keyToPos(k);
          if (!inBounds(b, r, c)) continue;
          const t = b.grid[r][c];
          if (isEmptyCell(t)) continue;
          clearedCells.push({ r, c, id: t.id, key: t.key || null, tile: { ...t } });
          b.grid[r][c] = null;
        }
        const mechanicFacts = _applyMechanicDamage(b, clearedCells);
        if (DEBUG_RESOLVE_LOGS) {
          _rzDbg('special_swap_clear', {
            iter,
            cause: starStar ? 'special_swap_star_star' : 'special_swap',
            activatedCount: activated.length,
            stripesActivated: _countStripeActivations(activated),
            clearedCells: clearedCells.length,
            toClear: toClear.size,
          });
        }

        // Scoring: activaciones sin match (cascadeIter = iter)
        const activationScore = scoreForActivations(activated, { cascadeIter: iter });
        totalScore += activationScore;

        // Emitimos un clear "especial" (sin matches)
        const stripesActivated = _countStripeActivations(activated);
        const stripesActivationBreakdown = _makeStripeActivationBreakdown(activated, starStar ? 'special_swap_star_star' : 'special_swap');
        allEvents.push({
          type: 'clear',
          matches: [],
          clearedCells,
          activated,
          stripesActivated,
          stripesActivationBreakdown,
          scoreDelta: activationScore,
          mechanicFacts,
          cause: starStar ? 'special_swap_star_star' : 'special_swap',
        });

        const clearedIds = clearedCells.map((c) => c.id).filter(Boolean);
        states.push(makeStateBase('clear', {
          board: cloneBoard(b),
          matches: [],
          clearedCells,
          activated,
          stripesActivated,
          stripesActivationBreakdown,
          clearedIds,
          scoreDelta: activationScore,
          mechanicFacts,
          cause: starStar ? 'special_swap_star_star' : 'special_swap',
        }));

        // gravity
       const anchorHole =
  ctx?.swapTo && Number.isFinite(ctx.swapTo.r) && Number.isFinite(ctx.swapTo.c)
    ? { r: ctx.swapTo.r, c: ctx.swapTo.c }
    : _readHoleMeta(b);

        const fallEvents = applyGravity(b, anchorHole);
        if (fallEvents.length) {
          allEvents.push({ type: 'gravity', fallEvents });
          states.push(makeStateBase('gravity', {
            board: cloneBoard(b),
            fallEvents,
            activated: [],
            stripesActivated: 0,
            stripesActivationBreakdown: ZERO_STRIPES,
            scoreDelta: 0,
          }));
        }

        // refill: always refill, but keep EXACTLY ONE hole so the player can swap into it.
        // This matches the desired gameplay: after any match, the board keeps 1 usable empty cell.
        const preferHole = ctx?.swapTo || (clearedCells.length ? { r: clearedCells[0].r, c: clearedCells[0].c } : null);
        const spawnEvents = refillKeepOneHole(b, rng, preferHole);
        if (spawnEvents.length) {
          allEvents.push({ type: 'refill', spawnEvents });
          states.push(makeStateBase('refill', { board: cloneBoard(b), spawnEvents, activated: [], stripesActivated: 0, stripesActivationBreakdown: ZERO_STRIPES, scoreDelta: 0 }));
        }

        // A partir de acá ya no queremos que el "ctx" del swap sesgue el anchor,
        // porque ya consumimos el swap especial.
        ctx = null;
      }
    }

   const matches = findMatches(b, iter === 0 ? ctx : null);
    if (DEBUG_RESOLVE_LOGS) {
      _rzDbg('matches', {
        iter,
        count: Array.isArray(matches) ? matches.length : 0,
        ctx: ctx ? { swapFrom: ctx.swapFrom, swapTo: ctx.swapTo, swapAxis: ctx.swapAxis } : null,
      });
    }
    if (!matches || matches.length === 0) break;

    // Scoring lines removed here

    // clear (with power-ups + activation)
    const clearedCells = [];
    const activated = []; // [{ special, at:{r,c}, targetKey? }]

    // 1) Build initial toClear set from matches (excluding anchor when creating a power-up)
    const toClear = new Set();
    const creationAnchors = new Set(); // positions that survive to become special (so we must not clear/activate them)
    // NOTE: Cosmic Charge now triggers an *immediate* activation (explosion/ray), not a persistent special.

    // Cosmic charge bookkeeping (carry across cascades)
    let chargeDelta = 0;

    for (const g of matches) {
      let anchor = null;
      // accumulate charge from EVERY match group (including simple 3s)
      chargeDelta += _chargeDeltaForGroup(g);
      // -------------------------------------------------------------------
      // Powerups must come ONLY from matcher (matches.js) so rules are consistent:
      // - 5+ in a straight line => STAR
      // - T/L shape => BOMB
      // - 4 in a straight line => STRIPE (with orient)
      // - 3 => none
      const cells = Array.isArray(g?.cells) ? g.cells : [];
      const cellsLen = cells.length;
      const providedPowerup = g?.powerup || g?.powerUp || g?.power_up || null;
      // Permitir powerups del matcher (bomb/star) aunque la forma no sea línea pura.
// Solo bloqueamos powerups si el matcher no envió ninguno.
const allowPowerupByLen =
  cellsLen >= 4 ||
  (providedPowerup && (providedPowerup.type === 'bomb' || providedPowerup.type === 'star'));

const effectivePowerup =
  providedPowerup && allowPowerupByLen ? providedPowerup : null;

      // Fallback orient only if matcher didn't provide one (should be rare)
      let inferredOrient = 'h';
      if (cellsLen >= 3) {
        const sameCol = cells.every((cc) => cc && cc.c === cells[0].c);
        inferredOrient = sameCol ? 'v' : 'h';
      }

      // Pick anchor:
      // Prefer ctx.swapTo if it is part of this group (ensures created special lands on moved tile)
      if (effectivePowerup && cellsLen > 0) {
        const swapTo = ctx?.swapTo;
        if (swapTo && typeof swapTo.r === 'number' && typeof swapTo.c === 'number') {
          const found = cells.find((cc) => cc && cc.r === swapTo.r && cc.c === swapTo.c);
          anchor = found || cells[cellsLen - 1];
        } else {
          anchor = cells[cellsLen - 1];
        }

        if (anchor) {
          const pType = String(effectivePowerup?.type || '').toLowerCase();

          // We want:
          //  - 4-match => STRIPE fires immediately (ray), not a persistent special.
          //  - 5-match => BOMB persists on the anchor.
          //  - 6+ => STAR persists on the anchor.
          if (pType === 'star' || pType === 'bomb') {
            pushUnique(creationAnchors, anchor.r, anchor.c);
          } else if (pType === 'stripe') {
            // Auto-fire created stripes: include the anchor in the clear set and expand the line.
            // (Do NOT mark it as a creation anchor, otherwise it would survive and never be cleared.)
            const orient =
              effectivePowerup && typeof effectivePowerup.orient === 'string'
                ? effectivePowerup.orient
                : inferredOrient;

            const special = orient === 'v' ? TILE_SPECIAL.STRIPE_V : TILE_SPECIAL.STRIPE_H;
            // Auto-fire created stripes: treat as an activation and record how many cells it adds.
            _activateStripe(b, toClear, activated, anchor.r, anchor.c, special, 'created_stripe_auto');
          }
        }
      } else if (cellsLen > 0) {
        anchor = cells[cellsLen - 1];
      }


      // Normalize powerup back onto group for later creation step
      // (but NEVER allow powerups for matches shorter than 4)
      // eslint-disable-next-line no-param-reassign
      g.powerup = allowPowerupByLen ? effectivePowerup : g.powerup;

      const pTypeLocal = String(g.powerup?.type || '').toLowerCase();

      for (const cell of cells) {
        const isAnchor = anchor && cell.r === anchor.r && cell.c === anchor.c;
        // STAR / BOMB preserve the anchor as a created special.
        // STRIPE (4-match) auto-fires and is cleared, so we do NOT skip its anchor here.
        if (isAnchor && (pTypeLocal === 'star' || pTypeLocal === 'bomb')) continue;

        pushUnique(toClear, cell.r, cell.c);
      }
    }

    // 2) Expand clears by activating specials (supports chained activations within the same clear)
    // Star target: use the matched group's key when the star is part of that group.
    const starTargets = new Map(); // "r,c" -> key
    for (const g of matches) {
      for (const cell of g.cells) {
        const k = `${cell.r},${cell.c}`;
        // prefer a concrete matched key if available
        if (!starTargets.has(k)) starTargets.set(k, g.key);
      }
    }

    const queue = Array.from(toClear.values());
    const processed = new Set(); // positions processed for special expansion

    while (queue.length) {
      const posKey = queue.pop();
      if (processed.has(posKey)) continue;
      processed.add(posKey);

      const { r, c } = keyToPos(posKey);
      if (!inBounds(b, r, c)) continue;

      // Do not activate/clear creation anchors in this tick
      if (creationAnchors.has(posKey)) continue;

      const t = b.grid[r][c];
      if (!t) continue;

      if (t.special === TILE_SPECIAL.STRIPE_H || t.special === TILE_SPECIAL.STRIPE_V) {
        const beforeSize = toClear.size;
        // This will expand the clear set and also record `clearedCount`
        _activateStripe(b, toClear, activated, r, c, t.special);

        // push newly added cells into queue
        if (toClear.size > beforeSize) {
          for (const nk of toClear.values()) {
            if (!processed.has(nk)) queue.push(nk);
          }
        }
      } else if (t.special === TILE_SPECIAL.STAR) {
        const targetKey = starTargets.get(posKey) || null;
        const beforeSize = toClear.size;
        _activateStar(b, toClear, activated, r, c, targetKey);

        if (toClear.size > beforeSize) {
          for (const nk of toClear.values()) {
            if (!processed.has(nk)) queue.push(nk);
          }
        }
      } else if (t.special === TILE_SPECIAL.BOMB) {
        const beforeSize = toClear.size;
        _activateBomb(b, toClear, activated, r, c);

        if (toClear.size > beforeSize) {
          for (const nk of toClear.values()) {
            if (!processed.has(nk)) queue.push(nk);
          }
        }
      }
    }

    // Scoring (Candy-like): matches + activations, scaled by cascade iteration
    const matchScore = sumMatchScores(matches, { cascadeIter: iter });
    const activationScore = scoreForActivations(activated, { cascadeIter: iter });
    const scoreDelta = matchScore + activationScore;
    totalScore += scoreDelta;

    // 3) Apply clear: create power-ups at anchors, then null out everything in toClear (excluding anchors)
    for (const g of matches) {
      // Safety: never create specials for matches shorter than 4.
      if ((!g.powerup || !Array.isArray(g.cells) || g.cells.length < 4) && g.powerup == null) continue;
      const swapTo = ctx?.swapTo;
      const anchor = (swapTo && Array.isArray(g.cells))
        ? (g.cells.find((cc) => cc && cc.r === swapTo.r && cc.c === swapTo.c) || g.cells[g.cells.length - 1])
        : g.cells[g.cells.length - 1];
      if (!anchor) continue;

      const t = b.grid[anchor.r][anchor.c];
      if (!t) continue;

      // transform into special instead of clearing
      const p = g.powerup || {};
      const pType = String(p.type || '').toLowerCase();
      if (pType === 'star') {
        t.special = TILE_SPECIAL.STAR;
      } else if (pType === 'bomb') {
        t.special = TILE_SPECIAL.BOMB;
      }
    }

    for (const k of toClear.values()) {
      if (creationAnchors.has(k)) continue; // survive as created special
      const { r, c } = keyToPos(k);
      if (!inBounds(b, r, c)) continue;

      const t = b.grid[r][c];
      if (isEmptyCell(t)) continue;

      clearedCells.push({ r, c, id: t.id, key: t.key || null, tile: { ...t } });
      b.grid[r][c] = null;
    }

    // Persist charge accumulation (AFTER real clear)
    const chargeAfterClear = _readCharge(b) + chargeDelta;

    let chargeTriggered = false;
    let chargeTriggeredSpecial = null;
    let chargeTriggeredAt = null;

    if (chargeAfterClear >= COSMIC_CHARGE_THRESHOLD) {
      chargeTriggered = true;

      const remainder = chargeAfterClear % COSMIC_CHARGE_THRESHOLD;

      // Choose anchor deterministically: reuse first cleared cell
      const anchor = clearedCells.length ? { r: clearedCells[0].r, c: clearedCells[0].c } : null;

      if (anchor && inBounds(b, anchor.r, anchor.c)) {
        chargeTriggeredAt = anchor;

        const special = _pickChargeSpecial(rng, null);
        chargeTriggeredSpecial = special;

        // IMPORTANT: charge-trigger activations must actually clear cells.
        // We want activation bookkeeping (clearedCount / score) to reflect ONLY the *new* cells
        // added by the charge trigger, not the cells already being cleared by the normal match.
        const toClearBeforeCharge = new Set(toClear);

        // Start from the existing clear-set so `_activateStripe/_activateBomb` can compute
        // how many *new* cells were added by the charge trigger.
        const chargeToClear = new Set(toClearBeforeCharge);
        chargeToClear.add(`${anchor.r},${anchor.c}`);

        if (special === TILE_SPECIAL.STRIPE_H || special === TILE_SPECIAL.STRIPE_V) {
          _activateStripe(b, chargeToClear, activated, anchor.r, anchor.c, special, 'charge_trigger');
        } else if (special === TILE_SPECIAL.BOMB) {
          _activateBomb(b, chargeToClear, activated, anchor.r, anchor.c, 'charge_trigger');
        }

        // Apply ONLY the newly-added cells (skip anything that was already in the normal clear)
        // so we don't double-count / waste work.
        for (const k of chargeToClear.values()) {
          if (toClearBeforeCharge.has(k)) continue;

          toClear.add(k);
          if (creationAnchors.has(k)) continue;

          const { r, c } = keyToPos(k);
          if (!inBounds(b, r, c)) continue;

          const t2 = b.grid[r][c];
          if (isEmptyCell(t2)) continue;

          clearedCells.push({ r, c, id: t2.id, key: t2.key || null, tile: { ...t2 } });
          b.grid[r][c] = null;
        }
      }

      _writeCharge(b, remainder);
    } else {
      _writeCharge(b, chargeAfterClear);
    }
    if (DEBUG_RESOLVE_LOGS) {
      _rzDbg('clear_applied', {
        iter,
        matchGroups: Array.isArray(matches) ? matches.length : 0,
        toClear: toClear.size,
        clearedCells: clearedCells.length,
        activated: activated.length,
        stripesActivated: _countStripeActivations(activated),
        chargeTriggered: !!chargeTriggered,
        chargeSpecial: chargeTriggeredSpecial || null,
        chargeAt: chargeTriggeredAt || null,
      });
    }

    const mechanicFacts = _applyMechanicDamage(b, clearedCells);

    const stripesActivated = _countStripeActivations(activated);
    const stripesActivationBreakdown = _makeStripeActivationBreakdown(activated, null);
    allEvents.push({
      type: 'clear',
      matches,
      clearedCells,
      activated,
      stripesActivated,
      stripesActivationBreakdown,
      scoreDelta,
      mechanicFacts,
      charge: {
        threshold: COSMIC_CHARGE_THRESHOLD,
        triggered: !!chargeTriggered,
        special: chargeTriggeredSpecial || null,
        at: chargeTriggeredAt || null,
      },
    });

    const clearedIds = clearedCells.map((c) => c.id).filter(Boolean);
    states.push(makeStateBase('clear', {
      board: cloneBoard(b),
      matches,
      clearedCells,
      activated,
      stripesActivated,
      stripesActivationBreakdown,
      clearedIds,
      scoreDelta,
      mechanicFacts,
      charge: {
        threshold: COSMIC_CHARGE_THRESHOLD,
        triggered: !!chargeTriggered,
        special: chargeTriggeredSpecial || null,
        at: chargeTriggeredAt || null,
      },
    }));

    // gravity (ALWAYS after a normal clear)
    const anchorHole =
      ctx?.swapTo && Number.isFinite(ctx.swapTo.r) && Number.isFinite(ctx.swapTo.c)
        ? { r: ctx.swapTo.r, c: ctx.swapTo.c }
        : (clearedCells.length ? { r: clearedCells[0].r, c: clearedCells[0].c } : _readHoleMeta(b));

    const fallEvents = applyGravity(b, anchorHole);
    if (DEBUG_RESOLVE_LOGS) {
      _rzDbg('gravity', { iter, fallEvents: fallEvents.length, anchorHole });
    }
    if (fallEvents.length) {
      allEvents.push({ type: 'gravity', fallEvents });
      states.push(makeStateBase('gravity', {
        board: cloneBoard(b),
        fallEvents,
        activated: [],
        stripesActivated: 0,
        stripesActivationBreakdown: ZERO_STRIPES,
        scoreDelta: 0,
      }));
    }

    // refill: always refill, but keep EXACTLY ONE hole so the player can swap into it.
    // Prevents accumulating multiple unusable empty cells.
    const preferHole = ctx?.swapTo || (clearedCells.length ? { r: clearedCells[0].r, c: clearedCells[0].c } : null);
    const spawnEvents = refillKeepOneHole(b, rng, preferHole);
    if (DEBUG_RESOLVE_LOGS) {
      _rzDbg('refill', { iter, spawns: spawnEvents.length, preferHole, hole: _readHoleMeta(b) });
    }
    if (spawnEvents.length) {
      allEvents.push({ type: 'refill', spawnEvents });
      states.push(makeStateBase('refill', {
        board: cloneBoard(b),
        spawnEvents,
        activated: [],
        stripesActivated: 0,
        stripesActivationBreakdown: ZERO_STRIPES,
        scoreDelta: 0,
      }));
    }

    // Si el tablero queda sin movimientos válidos (o queda con matches inmediatos), shuffle automático (tipo Candy)
    if (!_hasAnyPlayableMove(b) || _hasImmediateMatches(b)) {
      const beforeRef = b;
      const fix = ensurePlayableRestingBoard(b, rng, { maxShuffles: 35 });
      b = fix.board;

      if (fix.changed) {
        allEvents.push({ type: 'shuffle', reason: fix.reason || 'shuffle' });
        states.push(makeStateBase('shuffle', { board: cloneBoard(b), reason: fix.reason || 'shuffle', activated: [], stripesActivated: 0, stripesActivationBreakdown: ZERO_STRIPES, scoreDelta: 0 }));
      } else if (b !== beforeRef) {
        // Extremely defensive: if implementation ever returns a new ref without reporting change
        allEvents.push({ type: 'shuffle', reason: 'shuffle' });
        states.push(makeStateBase('shuffle', { board: cloneBoard(b), reason: 'shuffle', activated: [], stripesActivated: 0, stripesActivationBreakdown: ZERO_STRIPES, scoreDelta: 0 }));
      }
    }
  }

  // Final safety: never return a dead (no-moves) or auto-matching board.
  if (!_hasAnyPlayableMove(b) || _hasImmediateMatches(b)) {
    const fix = ensurePlayableRestingBoard(b, rng, { maxShuffles: 35 });
    b = fix.board;
    if (fix.changed) {
      allEvents.push({ type: 'shuffle', reason: fix.reason || 'shuffle' });
      states.push(makeStateBase('shuffle', { board: cloneBoard(b), reason: fix.reason || 'shuffle', activated: [], stripesActivated: 0, stripesActivationBreakdown: ZERO_STRIPES, scoreDelta: 0 }));
    }
  }

  _synchronizeHoleMeta(b);

  if (DEBUG_RESOLVE_LOGS) {
    _rzDbg('end', {
      totalScore,
      events: allEvents.length,
      states: states.length,
      finalHole: _readHoleMeta(b),
      finalCharge: b?.meta?.charge,
    });
  }
  return { board: b, scoreDelta: totalScore, events: allEvents, states };
}
